import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, Phone, ShieldCheck, Users } from 'lucide-react';
import {
    ersthelfer, notfallStellen, planeUebernahme, uebernahmeAendertEtwas,
    vorlagenAusProjekt, type UebernahmePlan,
} from '../bestandUebernahme';
import type { ProjektBestand } from '../useProjektBestand';
import type { ReportMetadata } from '../../../types';
import type { Beteiligter } from '../types';
import { Untertitel } from './bausteine';

/**
 * „Übernahme aus dem Bestand" — Vertreterliste und Notrufplan.
 *
 * ═══ WARUM DAS EINE KOPIERT WIRD UND DAS ANDERE NICHT ═════════════════════
 * Die Vertreterliste wird UEBERNOMMEN (`asplan_beteiligte.quelle='projekt'`):
 * der A+S-Plan wird freigegeben und eingefroren, er behauptet einen Stand.
 * Der Notrufplan wird nur GELESEN: es gibt genau einen je Projekt, und der
 * gehoert dem SiGe-Plan. Eine zweite Pflegestelle fuer eine Notfallnummer ist
 * schlimmer als keine — sie veraltet unbemerkt.
 *
 * ═══ WARUM HIER EIN KNOPF STEHT UND KEIN EFFEKT ═══════════════════════════
 * Uebernommen wird ausschliesslich auf ausdrueckliche Aktion. Eine
 * Vorbelegung, die sich beim Laden selbst speichert, mutiert
 * Bestandsdatensaetze rueckwirkend — in diesem Repo schon zweimal passiert
 * (Handbuch, Planungsphase). Gelesen wird beim Laden, geschrieben nie.
 *
 * Die Rechnung selbst steht ohne React in `../bestandUebernahme.ts` und ist
 * dort geprueft; diese Datei zeigt nur an und ruft auf.
 *
 * ═══ GELESEN WIRD IM CONTAINER, NICHT HIER ════════════════════════════════
 * Diese Karte rief `useProjektBestand` frueher selbst auf — neben der
 * Notfallkarte und dem Aushangreiter. Drei Staende derselben Quelle koennen
 * auseinanderlaufen, und bei Notfallnummern faellt das niemandem auf. Gelesen
 * wird jetzt einmal in `AsplanModul.tsx`; hier kommt das Ergebnis samt
 * `bereit` als Prop an.
 */

const KASTEN = 'bg-white border border-slate-200 rounded-2xl p-4 mb-4';

/**
 * Wie ein Beteiligter in der Vorschau heisst.
 *
 * Bei einem Menschen der Name, bei einer Firma der Firmenname — die
 * Auftraggeberzeile hat oft gar keinen Ansprechpartner, und „ — wird
 * angelegt" ohne davorstehenden Namen ist keine Vorschau.
 */
const bezeichnung = (p: { person: string; organisation: string }): string =>
    p.person || p.organisation || '(ohne Namen)';

interface Props {
    /** Stammdaten und Notrufplan — im Container EINMAL gelesen. */
    bestand: ProjektBestand;
    beteiligte: Beteiligter[];
    gesperrt: boolean;
    uebernehmen: (metadata: Partial<ReportMetadata>) => Promise<UebernahmePlan | null>;
}

export const BestandKarte: React.FC<Props> = ({
    bestand, beteiligte, gesperrt, uebernehmen,
}) => {
    const { bereit, metadata, notrufplan } = bestand;
    const [arbeitet, setArbeitet] = useState(false);
    const [meldung, setMeldung] = useState('');

    // Die Vorschau rechnet bei jeder Aenderung der Beteiligtenliste neu — sie
    // zeigt immer den Stand, den ein Klick JETZT erzeugen wuerde.
    const vorhaben = useMemo(
        () => planeUebernahme(metadata, beteiligte),
        [metadata, beteiligte]);
    const ausProjekt = useMemo(() => vorlagenAusProjekt(metadata), [metadata]);

    const stellen = useMemo(() => notfallStellen(notrufplan), [notrufplan]);
    const helfer = useMemo(() => ersthelfer(notrufplan), [notrufplan]);

    const ausloesen = useCallback(async () => {
        setArbeitet(true);
        setMeldung('');
        const erg = await uebernehmen(metadata || {});
        setArbeitet(false);
        if (!erg) return;
        const teile: string[] = [];
        if (erg.neu.length > 0) teile.push(`${erg.neu.length} übernommen`);
        if (erg.aktualisieren.length > 0) teile.push(`${erg.aktualisieren.length} aktualisiert`);
        setMeldung(teile.length > 0
            ? `${teile.join(', ')}.`
            : 'Es gab nichts zu übernehmen, der Stand war bereits gleich.');
    }, [uebernehmen, metadata]);

    return (
        <section className={KASTEN}>
            <h2 className="font-bold text-slate-800 mb-0.5">Aus dem Projekt übernehmen</h2>
            <p className="text-xs text-slate-500 mb-3">
                Vertreterliste, Bauherr und Auftraggeber werden übernommen und tragen danach
                die Herkunft „Projekt“. Der Notrufplan wird nur gelesen; geändert wird er
                im SiGe-Plan.
            </p>

            {!bereit ? (
                <p className="text-sm text-slate-400 py-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Projektdaten werden gelesen …
                </p>
            ) : (
                <>
                    {/* ─── Beteiligte aus dem Projekt ─────────────────────── */}
                    <Untertitel
                        text="Beteiligte aus dem Projekt"
                        hinweis="Koordinator und Vertretungen nach BaustellV sowie Bauherr und Auftraggeber. Der Koordinator nach DGUV Regel 101-004 ist eine andere Bestellung und bleibt eine Zeile von Hand."
                    />

                    {ausProjekt.length === 0 ? (
                        <p className="text-sm text-slate-400 py-2">
                            Im Projekt ist weder ein Koordinator noch ein Bauherr eingetragen.
                        </p>
                    ) : (
                        <ul className="text-sm space-y-1 mb-3">
                            {vorhaben.neu.map(v => (
                                <li key={`neu_${v.funktion_key}_${bezeichnung(v)}`} className="text-slate-700">
                                    <b>{bezeichnung(v)}</b>
                                    <span className="text-emerald-700">: wird angelegt ({v.funktion})</span>
                                </li>
                            ))}
                            {vorhaben.aktualisieren.map(a => (
                                <li key={`upd_${a.id}`} className="text-slate-700">
                                    <b>{a.person}</b>
                                    <span className="text-sky-700">
                                        : wird nachgezogen ({Object.keys(a.aenderung).join(', ')})
                                    </span>
                                </li>
                            ))}
                            {vorhaben.unveraendert.map(v => (
                                <li key={`gleich_${v.funktion_key}_${bezeichnung(v)}`} className="text-slate-400">
                                    {bezeichnung(v)}: unverändert
                                </li>
                            ))}
                            {vorhaben.bereitsManuell.map(m => (
                                <li key={`hand_${m.id}`} className="text-slate-500">
                                    {bezeichnung(m)}: steht bereits von Hand in der Liste und wird nicht angefasst
                                </li>
                            ))}
                        </ul>
                    )}

                    {vorhaben.entfallen.length > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mb-3 flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                            <span>
                                {vorhaben.entfallen.map(bezeichnung).join(', ')}
                                {vorhaben.entfallen.length === 1
                                    ? ' steht nicht mehr in den Projektdaten.'
                                    : ' stehen nicht mehr in den Projektdaten.'}
                                {' '}Die Zeile bleibt stehen, denn hier kann eine von Hand ergänzte Angabe
                                darinstehen. Löschen ist eine Entscheidung, keine Nebenwirkung.
                            </span>
                        </p>
                    )}

                    {!gesperrt && (
                        <div className="flex items-center gap-3 mb-4">
                            <button
                                type="button"
                                onClick={() => void ausloesen()}
                                disabled={arbeitet || !uebernahmeAendertEtwas(vorhaben)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white
                                           rounded-lg text-xs font-bold disabled:bg-slate-200 disabled:text-slate-400"
                            >
                                {arbeitet
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Users className="w-3.5 h-3.5" />}
                                Aus dem Projekt übernehmen
                            </button>
                            {meldung && (
                                <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                                    <Check className="w-3.5 h-3.5" /> {meldung}
                                </span>
                            )}
                        </div>
                    )}

                    {/* ─── Notrufplan ─────────────────────────────────────── */}
                    <Untertitel
                        text="Notfallstellen aus dem Notrufplan"
                        hinweis="Quelle ist der Notrufplan des SiGe-Plans: eine Quelle, hier nur gelesen. Ein zweiter Pflegeort für eine Notfallnummer veraltet unbemerkt."
                    />

                    {stellen.length === 0 ? (
                        <p className="text-sm text-slate-400 py-2">
                            Im SiGe-Plan ist noch keine Notfallstelle hinterlegt.
                        </p>
                    ) : (
                        <ul className="space-y-1.5 mb-3">
                            {stellen.map(s => (
                                <li key={s.key} className="text-sm flex gap-2">
                                    <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                                    <span>
                                        <b className="text-slate-800">{s.was}:</b>{' '}
                                        <span className="text-slate-700">{s.name || '(ohne Namen)'}</span>
                                        {s.telefon && <span className="font-semibold text-slate-900"> · {s.telefon}</span>}
                                        {s.entfernung && <span className="text-slate-500"> · {s.entfernung}</span>}
                                        {s.geprueft && (
                                            <span className="inline-flex items-center gap-0.5 text-emerald-700 text-xs ml-1">
                                                <ShieldCheck className="w-3 h-3" /> geprüft
                                            </span>
                                        )}
                                        {s.adresse && (
                                            <span className="block text-xs text-slate-500">{s.adresse}</span>
                                        )}
                                        {s.hinweis && (
                                            <span className="block text-xs text-amber-700">{s.hinweis}</span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {helfer.length > 0 && (
                        <p className="text-xs text-slate-600 mb-3">
                            <b>Ersthelfer:</b>{' '}
                            {helfer.map(h => h.firma ? `${h.name} (${h.firma})` : h.name).join(', ')}
                        </p>
                    )}

                </>
            )}
        </section>
    );
};
